import React from "react";
import { Link } from "react-router";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import type { IncidentVm } from "@/features/daily/components/command/command-view-model";
import type { AttentionCountsVm, HealthCauseVm, HealthCausesVm, IncidentRegisterVm } from "@/features/daily/components/command/health-view-model";
import { IncidentsPanel } from "@/features/daily/components/incidents/IncidentsPanel";

type Props = {
    register: IncidentRegisterVm;
    incidents: IncidentVm[];
    viewerId: string;
    onApplied: () => void;
    causes: HealthCausesVm | null;
    counts: AttentionCountsVm;
};

const counted = (count: number, singular: string, plural: string): string => `${count} ${count === 1 ? singular : plural}`;

const AttentionList: React.FC<{ causes: HealthCauseVm[]; label: string }> = ({ causes, label }) => (
    <ul className="daily-cc-attention__list" aria-label={label}>
        {causes.map((cause) => (
            <li key={cause.key} className="daily-cc-attention__item" data-tone={cause.tone}>
                <DailyIcon name={cause.tone === "critical" ? "error" : "warning"} />
                <div className="daily-cc-attention__body">
                    <span className="daily-cc-attention__title">{cause.text}</span>
                    <Link className="daily-link" to={cause.href}>
                        Abrir la cola <DailyIcon name="arrow_forward" />
                    </Link>
                </div>
            </li>
        ))}
    </ul>
);

/**
 * FR-010/FR-011 + vb-metrics#38 U5: what needs a person now — the live checks off target,
 * the open incidents with their actions, and the informative causes with the way back to
 * normal. The page mounts it only while one of them exists.
 */
export const AttentionPanel: React.FC<Props> = ({ register, incidents, viewerId, onApplied, causes, counts }) => (
    <section className="daily-panel daily-cc-attention" aria-labelledby="daily-attention-title">
        <div className="daily-panel__head">
            <h2 id="daily-attention-title">Necesita atención</h2>
            <span className="daily-cc-attention__badges">
                {counts.critical === 0 ? null : <span className="daily-pill" data-tone="critical">{counted(counts.critical, "crítica", "críticas")}</span>}
                {counts.warning === 0 ? null : <span className="daily-pill" data-tone="warning">{counted(counts.warning, "aviso", "avisos")}</span>}
                {counts.openIncidents === 0 ? null : <span className="daily-pill" data-tone="warning">{counted(counts.openIncidents, "incidencia abierta", "incidencias abiertas")}</span>}
                {register.status === "unavailable" ? <span className="daily-pill">Registro no disponible</span> : null}
            </span>
        </div>
        {causes === null || causes.affecting.length === 0 ? null : <AttentionList causes={causes.affecting} label="Afecta al estado" />}
        {causes === null || causes.warnings.length === 0 ? null : <AttentionList causes={causes.warnings} label="Avisos" />}
        {register.status === "available" && incidents.length > 0 ? (
            <IncidentsPanel incidents={incidents} viewerId={viewerId} onApplied={onApplied} empty={null} />
        ) : null}
        {causes === null || causes.informative.length === 0 ? null : (
            <>
                <p className="daily-cc-attention__group">Informativo ({causes.informative.length})</p>
                <ul className="daily-cc-attention__list" aria-label="Informativo">
                    {causes.informative.map((cause) => (
                        <li key={cause.key} className="daily-cc-attention__info" data-tone={cause.tone}>
                            <span className="daily-cc-attention__title">{cause.text}</span>
                            <span className="daily-cc-attention__normal">
                                <strong>Cómo volver a la normalidad:</strong> {cause.normalization}
                            </span>
                        </li>
                    ))}
                </ul>
            </>
        )}
        {register.status === "unavailable" && causes !== null ? (
            <p className="daily-small daily-muted">{register.title}: las causas se calculan en vivo en cada consulta.</p>
        ) : null}
    </section>
);
